import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink
} from 'firebase/auth';
import { auth, db, functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';

// Authentication Services
export const authService = {
  // Sign up new business
  async signUpBusiness(userData) {
    try {
      const { email, password, ...businessData } = userData;
      
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update user profile
      await updateProfile(user, {
        displayName: businessData.businessName
      });
      
      // Create business document in Firestore
      const businessDoc = {
        name: businessData.ownerName,
        businessname: businessData.businessName,
        email: email,
        phone: businessData.phone || '',
        country: businessData.country || 'Canada',
        industry: businessData.businessType,
        plantype: 'free',
        associatedtags: {
          [businessData.tagNumber]: {
            activate: true,
            lastCTR: 0
          }
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(doc(db, 'businesses', user.uid), businessDoc);
      
      // Create Settings subcollection
      const settingsRef = doc(db, 'businesses', user.uid, 'Settings', 'config');
      await setDoc(settingsRef, {
        brandlogo: '',
        brandcolour: '#FFA366',
        pointsforreward: 1200,
        pointspertap: 10,
        rewarddescription: 'a free reward'
      });
      
      return { user, businessData: businessDoc };
    } catch (error) {
      console.error('Error signing up business:', error);
      throw error;
    }
  },
  
  // Sign in business
  async signInBusiness(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  },
  
  // Sign out
  async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  },

  // Customer Auth - Send passwordless email link
  async sendPasswordlessEmail(email, redirectUrl) {
    try {
      // Parse current URL to preserve sid parameter and add email
      const url = new URL(redirectUrl || window.location.href);
      const sessionId = url.searchParams.get('sid');
      
      // Build clean redirect URL with email and sid
      const baseUrl = `${url.origin}${url.pathname}`;
      const redirectWithParams = `${baseUrl}?sid=${sessionId}&email=${encodeURIComponent(email)}`;
      
      const actionCodeSettings = {
        // URL must be in Firebase Console authorized domains
        url: redirectWithParams,
        // This must be true for email link sign-in
        handleCodeInApp: true,
      };

      console.log('[authService] Sending passwordless email to:', email);
      console.log('[authService] Redirect URL:', redirectWithParams);
      
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Still save to localStorage as backup
      window.localStorage.setItem('emailForSignIn', email);
      
      console.log('[authService] Passwordless email sent successfully');
      return { success: true };
    } catch (error) {
      console.error('[authService] Error sending passwordless email:', error);
      console.error('[authService] Error code:', error.code);
      console.error('[authService] Error message:', error.message);
      throw error;
    }
  },

  // Customer Auth - Complete passwordless sign-in
  async completePasswordlessSignIn(emailLink, email) {
    try {
      // Verify the link is valid
      if (!isSignInWithEmailLink(auth, emailLink)) {
        throw new Error('Invalid sign-in link');
      }

      // Get email from parameter or localStorage
      let userEmail = email;
      if (!userEmail) {
        userEmail = window.localStorage.getItem('emailForSignIn');
      }

      if (!userEmail) {
        throw new Error('Email not found. Please enter your email again.');
      }

      // Sign in with email link
      const userCredential = await signInWithEmailLink(auth, userEmail, emailLink);
      console.log('[authService] Email sign-in successful:', userCredential.user.uid);
      
      // Clear email from storage
      window.localStorage.removeItem('emailForSignIn');
      
      return userCredential.user;
    } catch (error) {
      console.error('Error completing passwordless sign-in:', error);
      throw error;
    }
  },

  // Create customer profile in Firestore
  async createCustomerProfile(customerUid, businessUid, email) {
    try {
      const customerRef = doc(db, 'customers', customerUid);
      const customerDoc = await getDoc(customerRef);
      
      // Only create if doesn't exist
      if (!customerDoc.exists()) {
        await setDoc(customerRef, {
          email: email || '',
          createdAt: serverTimestamp()
        });
        console.log('[authService] Customer profile created:', customerUid);
      }

      // Create business association if businessUid provided
      if (businessUid) {
        const associationRef = doc(db, 'customers', customerUid, 'businessAssociations', businessUid);
        await setDoc(associationRef, {
          pointsBalance: 0,
          joinedWhen: serverTimestamp()
        }, { merge: true });
        console.log('[authService] Business association created:', businessUid);
      }

      return { success: true };
    } catch (error) {
      console.error('Error creating customer profile:', error);
      throw error;
    }
  }
};

// Business Data Services
export const businessService = {
  // Get business data
  async getBusinessData(businessUid) {
    try {
      console.log('[businessService] Attempting to get business:', businessUid);
      console.log('[businessService] Database instance:', db);
      
      const businessDoc = await getDoc(doc(db, 'businesses', businessUid));
      console.log('[businessService] Document fetch completed. Exists:', businessDoc.exists());
      
      if (businessDoc.exists()) {
        const data = businessDoc.data();
        console.log('[businessService] Business data:', data);
        
        // Fetch Settings subcollection
        const settingsDoc = await getDoc(doc(db, 'businesses', businessUid, 'Settings', 'config'));
        const settings = settingsDoc.exists() ? settingsDoc.data() : {
          brandlogo: '',
          brandcolour: '#FFA366',
          pointsforreward: 1200,
          pointspertap: 10,
          rewarddescription: 'a free reward'
        };
        console.log('[businessService] Settings data:', settings);
        
        return { id: businessDoc.id, ...data, settings };
      }
      
      console.log('[businessService] Business document does not exist');
      return null;
    } catch (error) {
      console.error('[businessService] ERROR getting business data:', error);
      console.error('[businessService] Error code:', error.code);
      console.error('[businessService] Error message:', error.message);
      throw error;
    }
  },
  
  // Update business settings
  async updateBusinessSettings(businessUid, settings) {
    try {
      const settingsRef = doc(db, 'businesses', businessUid, 'Settings', 'config');
      await setDoc(settingsRef, settings, { merge: true });
      
      // Update parent document timestamp
      await updateDoc(doc(db, 'businesses', businessUid), {
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating business settings:', error);
      throw error;
    }
  },
  
  // Get reward members for a business
  async getRewardMembers(businessUid, limitCount = 100) {
    try {
      const membersRef = collection(db, 'businesses', businessUid, 'rewardMembers');
      const q = query(membersRef, orderBy('dateJoined', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting reward members:', error);
      throw error;
    }
  },
  
  // Get business transactions
  async getBusinessTransactions(businessUid, limitCount = 100) {
    try {
      const transactionsRef = collection(db, 'allTransactions', businessUid, 'transactions');
      const q = query(transactionsRef, orderBy('timestamp', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting business transactions:', error);
      return []; // Return empty array if collection doesn't exist yet
    }
  }
};

// Tap Session Services
export const tapSessionService = {
  // Get tap session data
  async getTapSession(sessionId) {
    try {
      console.log('[tapSessionService] Attempting to get session:', sessionId);
      console.log('[tapSessionService] Database instance:', db);
      
      const sessionDoc = await getDoc(doc(db, 'tapSessions', sessionId));
      console.log('[tapSessionService] Document fetch completed. Exists:', sessionDoc.exists());
      
      if (sessionDoc.exists()) {
        const data = sessionDoc.data();
        console.log('[tapSessionService] Session data:', data);
        return { id: sessionDoc.id, ...data };
      }
      
      console.log('[tapSessionService] Session document does not exist');
      return null;
    } catch (error) {
      console.error('[tapSessionService] ERROR getting tap session:', error);
      console.error('[tapSessionService] Error code:', error.code);
      console.error('[tapSessionService] Error message:', error.message);
      throw error;
    }
  },

  // Claim a tap session (calls cloud function)
  async claimTap(sessionId) {
    try {
      console.log('[tapSessionService] Claiming tap session:', sessionId);
      const claimTapFunction = httpsCallable(functions, 'claimTap');
      const result = await claimTapFunction({ sid: sessionId });
      console.log('[tapSessionService] Claim successful:', result.data);
      return result.data;
    } catch (error) {
      console.error('[tapSessionService] ERROR claiming tap:', error);
      throw error;
    }
  }
};

// Customer Data Services
export const customerService = {
  // Get customer data with business associations
  async getCustomerData(customerUid) {
    try {
      const customerDoc = await getDoc(doc(db, 'customers', customerUid));
      if (!customerDoc.exists()) {
        return null;
      }
      
      // Fetch business associations subcollection
      const associationsRef = collection(db, 'customers', customerUid, 'businessAssociations');
      const associationsSnapshot = await getDocs(associationsRef);
      
      const businessAssociations = {};
      associationsSnapshot.docs.forEach(doc => {
        businessAssociations[doc.id] = doc.data();
      });
      
      return { 
        id: customerDoc.id, 
        ...customerDoc.data(),
        businessAssociations 
      };
    } catch (error) {
      console.error('Error getting customer data:', error);
      throw error;
    }
  },
  
  // Get customer business associations
  async getCustomerBusinessAssociations(customerUid) {
    try {
      const associationsRef = collection(db, 'customers', customerUid, 'businessAssociations');
      const querySnapshot = await getDocs(associationsRef);
      
      return querySnapshot.docs.map(doc => ({
        businessUid: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting customer business associations:', error);
      return [];
    }
  }
};

// Analytics Services
export const analyticsService = {
  // Calculate dashboard metrics from transactions
  calculateMetrics(transactions, members) {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const thisMonth = transactions.filter(t => {
      const transactionDate = new Date(t.timestamp.toDate());
      return transactionDate.getMonth() === currentMonth && 
             transactionDate.getFullYear() === currentYear;
    });
    
    const thisYear = transactions.filter(t => {
      const transactionDate = new Date(t.timestamp.toDate());
      return transactionDate.getFullYear() === currentYear;
    });
    
    const tapsThisMonth = thisMonth.filter(t => t.type === 'tap').length;
    const tapsThisYear = thisYear.filter(t => t.type === 'tap').length;
    const tapsAllTime = transactions.filter(t => t.type === 'tap').length;
    
    const redemptionsThisMonth = thisMonth.filter(t => t.type === 'redeem').length;
    const redemptionsThisYear = thisYear.filter(t => t.type === 'redeem').length;
    const redemptionsAllTime = transactions.filter(t => t.type === 'redeem').length;
    
    const joinedThisMonth = members.filter(m => {
      const joinedDate = new Date(m.dateJoined.toDate());
      return joinedDate.getMonth() === currentMonth && 
             joinedDate.getFullYear() === currentYear;
    }).length;
    
    const joinedThisYear = members.filter(m => {
      const joinedDate = new Date(m.dateJoined.toDate());
      return joinedDate.getFullYear() === currentYear;
    }).length;
    
    return {
      tapsthismonth: tapsThisMonth,
      tapsthisyear: tapsThisYear,
      tapsalltime: tapsAllTime,
      redemptionsthismonth: redemptionsThisMonth,
      redemptionsthisyear: redemptionsThisYear,
      redemptionsalltime: redemptionsAllTime,
      joinedthismonth: joinedThisMonth,
      joinedthisyear: joinedThisYear,
      joinedalltime: members.length,
      totalmembers: members.length
    };
  },
  
  // Generate time series data from transactions
  generateTimeSeriesData(transactions, members, period = 'thisyear') {
    // This would generate time series data similar to the example data
    // Implementation depends on the specific requirements
    const timeSeriesData = {
      taps: [],
      redemptions: [],
      joined: []
    };
    
    // Group transactions by date and calculate cumulative counts
    // This is a simplified version - you might want to implement more sophisticated logic
    
    return timeSeriesData;
  }
};

// Email Collection Services
export const emailCollectionService = {
  // Save submitted email to Emails Submitted collection
  async saveSubmittedEmail(email, businessUid = null) {
    try {
      const emailData = {
        email: email,
        submittedAt: serverTimestamp(),
        businessUid: businessUid
      };
      
      const emailsRef = collection(db, 'emailsSubmitted');
      await setDoc(doc(emailsRef), emailData);
      
      console.log('[emailCollectionService] Email saved:', email);
      return { success: true };
    } catch (error) {
      console.error('[emailCollectionService] Error saving email:', error);
      // Don't throw - this is not critical to the user flow
      return { success: false, error: error.message };
    }
  }
};
